import React, { useState } from "react";
import axios from "axios";
import { CalendarCheck, Loader2, XCircle, Video, MapPin } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DURATIONS = [30, 45, 60, 90, 120];
const TYPES = ["In-person business visit", "Virtual meeting"];

const fmtEastern = (iso) => new Date(iso).toLocaleString("en-US", { timeZone: "America/New_York", weekday: "long", month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) + " ET";
const toEasternParts = (iso) => {
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
  return { date: `${p.year}-${p.month}-${p.day}`, time: `${p.hour}:${p.minute}` };
};

export default function MeetingPanel({ lead, token, onChange }) {
  const { toast } = useToast();
  const m = lead.fields?.meeting;
  const scheduled = m?.status === "scheduled";
  const [editing, setEditing] = useState(!scheduled);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(() => ({
    ...(scheduled ? toEasternParts(m.start) : { date: "", time: "" }),
    duration_min: m?.duration_min || 60,
    meeting_type: m?.meeting_type || lead.fields?.meeting_type || TYPES[0],
    location: m?.location || lead.fields?.location || "",
    link: m?.link || "",
    note: "",
  }));
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const headers = { "X-Admin-Password": token };
  const virtual = form.meeting_type === "Virtual meeting";

  const save = async () => {
    if (!form.date || !form.time) return toast({ title: "Pick a date and time", variant: "destructive" });
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/leads/team/${lead.id}/meeting`, { ...form, duration_min: Number(form.duration_min), origin_url: window.location.origin }, { headers });
      onChange(data.meeting);
      setForm((f) => ({ ...f, note: "" }));
      setEditing(false);
      toast({ title: data.kind === "rescheduled" ? "Meeting rescheduled" : "Meeting confirmed", description: data.email_sent ? `Email sent to ${lead.email}` : "Saved, but the email could not be sent." });
    } catch (e) {
      toast({ title: "Could not save meeting", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    const reason = window.prompt(`Cancel this meeting and email ${lead.email}?\nOptional message to include:`, "");
    if (reason === null) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/leads/team/${lead.id}/meeting/cancel`, { note: reason.trim() || null }, { headers });
      onChange(data.meeting);
      setEditing(true);
      toast({ title: "Meeting cancelled", description: data.email_sent ? `Email sent to ${lead.email}` : "Saved, but the email could not be sent." });
    } catch (e) {
      toast({ title: "Could not cancel", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-5 rounded-xl border border-[color:var(--dms-peach)]/50 bg-white p-5" data-testid={`meeting-panel-${lead.id}`}>
      <p className="text-[10px] tracking-widest uppercase text-[color:var(--dms-navy)]/55 font-semibold flex items-center gap-2"><CalendarCheck className="w-3.5 h-3.5" /> Meeting · Eastern Time</p>
      {scheduled && !editing && (
        <div className="mt-3 flex items-start justify-between gap-4 flex-wrap" data-testid="meeting-summary">
          <div>
            <p className="font-serif text-lg text-[color:var(--dms-navy)]">{fmtEastern(m.start)}</p>
            <p className="text-sm text-[color:var(--dms-navy)]/65 mt-1 flex items-center gap-1.5">
              {m.meeting_type === "Virtual meeting" ? <Video className="w-3.5 h-3.5" /> : <MapPin className="w-3.5 h-3.5" />}
              {m.meeting_type} · {m.duration_min} min · {m.meeting_type === "Virtual meeting" ? m.link : m.location}
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setEditing(true)} className="px-4 py-2 rounded-full border border-[color:var(--dms-navy)]/20 text-xs font-semibold uppercase tracking-widest text-[color:var(--dms-navy)]" data-testid="meeting-reschedule-btn">Reschedule</button>
            <button onClick={cancel} disabled={busy} className="px-4 py-2 rounded-full border border-red-300 text-xs font-semibold uppercase tracking-widest text-red-700 inline-flex items-center gap-1" data-testid="meeting-cancel-btn"><XCircle className="w-3.5 h-3.5" /> Cancel</button>
          </div>
        </div>
      )}
      {m?.status === "cancelled" && <p className="mt-2 text-xs text-red-700" data-testid="meeting-cancelled-note">Previous meeting ({fmtEastern(m.start)}) was cancelled.</p>}
      {editing && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-3">
          <input type="date" value={form.date} onChange={set("date")} className="mp-field" data-testid="meeting-date" />
          <input type="time" value={form.time} onChange={set("time")} className="mp-field" data-testid="meeting-time" />
          <select value={form.duration_min} onChange={set("duration_min")} className="mp-field" data-testid="meeting-duration">
            {DURATIONS.map((d) => <option key={d} value={d}>{d} minutes</option>)}
          </select>
          <select value={form.meeting_type} onChange={set("meeting_type")} className="mp-field" data-testid="meeting-type">
            {TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
          {virtual
            ? <input value={form.link} onChange={set("link")} placeholder="Paste your Zoom or Google Meet link (https://…)" className="mp-field md:col-span-4" data-testid="meeting-link" />
            : <input value={form.location} onChange={set("location")} placeholder="Business address" className="mp-field md:col-span-4" data-testid="meeting-location" />}
          <input value={form.note} onChange={set("note")} placeholder="Optional note for the client" className="mp-field md:col-span-4" data-testid="meeting-note" />
          <div className="md:col-span-4 flex justify-end gap-2">
            {scheduled && <button onClick={() => setEditing(false)} className="px-4 py-2 text-xs font-semibold uppercase tracking-widest text-[color:var(--dms-navy)]/60" data-testid="meeting-edit-close">Keep current time</button>}
            <button onClick={save} disabled={busy} className="btn-peach" data-testid="meeting-save-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarCheck className="w-4 h-4" />} {scheduled ? "Save new time & email client" : "Confirm meeting & email client"}
            </button>
          </div>
        </div>
      )}
      <style>{`.mp-field{width:100%;border:1px solid rgba(26,47,77,.18);border-radius:10px;padding:9px 12px;font-size:13px;color:#1a2f4d;background:#fff;outline:none}.mp-field:focus{border-color:var(--dms-peach)}`}</style>
    </div>
  );
}
